import { supabase } from "./client";

/* ------------------------------------------------------------------ *
 * Live syncing of a group's mission sheet (see supabase/sql/groups.sql).
 * Everyone in a group edits the same rows; each sheet section subscribes
 * to its table here and folds the other members' changes into its state.
 * Row-level security decides whose rows a session is sent, so a member
 * only ever hears about their own group's sheet.
 * ------------------------------------------------------------------ */

export type RowChange<T> = { type: "INSERT" | "UPDATE"; row: T } | { type: "DELETE"; id: string };

let channelSeq = 0;

// Subscribes to inserts/updates/deletes on one table. `filter` (e.g.
// "group_id=eq.<uuid>") narrows inserts and updates; deletes always arrive
// with just the row id, whatever the filter. `onResync` runs when the
// connection comes back after a drop, so the caller can reload what it
// missed. Returns the unsubscribe function.
export function subscribeToRows<T extends { id: string }>(
  table: string,
  filter: string | undefined,
  onChange: (change: RowChange<T>) => void,
  onResync: () => void
): () => void {
  let subscribedBefore = false;

  const channel = supabase
    .channel(`sheet-${table}-${++channelSeq}`)
    .on("postgres_changes", { event: "*", schema: "public", table, ...(filter ? { filter } : {}) }, (payload) => {
      if (payload.eventType === "DELETE") {
        const id = (payload.old as { id?: string }).id;
        if (id) onChange({ type: "DELETE", id });
        return;
      }
      onChange({ type: payload.eventType, row: payload.new as T });
    })
    .subscribe((status) => {
      if (status !== "SUBSCRIBED") return;
      if (subscribedBefore) onResync();
      subscribedBefore = true;
    });

  return () => {
    supabase.removeChannel(channel);
  };
}

// Replaces the row with the same id, or appends it. Used for both local
// inserts and incoming ones, since a member's own insert also comes back
// through the subscription.
export function upsertById<T extends { id: string }>(list: T[], row: T): T[] {
  return list.some((item) => item.id === row.id) ? list.map((item) => (item.id === row.id ? row : item)) : [...list, row];
}

const PENDING_TTL_MS = 5000;

// Tracks fields this browser has edited but the server hasn't echoed back
// yet. Saves are debounced, so an echo of an older keystroke (or another
// member's change to a different field of the same row) can arrive while
// the user is still typing; merge() keeps the local value for those fields
// instead of letting the incoming row yank the text back.
export function createPendingEdits() {
  const edits = new Map<string, Record<string, { value: unknown; at: number }>>();

  return {
    mark(id: string, patch: Record<string, unknown>) {
      const fields = edits.get(id) ?? {};
      const at = Date.now();
      for (const [field, value] of Object.entries(patch)) fields[field] = { value, at };
      edits.set(id, fields);
    },

    merge<T extends object>(id: string, remote: T): T {
      const fields = edits.get(id);
      if (!fields) return remote;

      const merged = { ...remote } as Record<string, unknown>;
      for (const [field, edit] of Object.entries(fields)) {
        // Confirmed by the server, or so old the save must have failed.
        if (merged[field] === edit.value || Date.now() - edit.at > PENDING_TTL_MS) {
          delete fields[field];
          continue;
        }
        merged[field] = edit.value;
      }
      return merged as T;
    },

    forget(id: string) {
      edits.delete(id);
    },
  };
}
